import React, { ReactNode } from 'react';
import { useTheme } from './ThemeProvider';
import SpaceBackground from './SpaceBackground';

export interface PageContainerProps {
  children: ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  spacing?: 'tight' | 'normal' | 'loose';
  backgroundIntensity?: 'none' | 'minimal' | 'normal' | 'cosmic';
  showParticles?: boolean;
  fullHeight?: boolean;
  glassmorphism?: boolean;
}

export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  className = '',
  maxWidth = 'lg',
  spacing = 'normal',
  backgroundIntensity = 'normal',
  showParticles = true,
  fullHeight = false,
  glassmorphism = false,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || theme === 'mixed';

  // Responsive max-width classes
  const maxWidthClasses = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    '2xl': 'max-w-8xl',
    full: 'max-w-full',
  };

  // Spacing classes
  const spacingClasses = {
    tight: 'px-4 py-4',
    normal: 'px-4 sm:px-6 lg:px-8 py-8',
    loose: 'px-6 sm:px-8 lg:px-12 py-12',
  };

  // Container styles
  const containerStyles = glassmorphism
    ? {
        background: 'var(--space-surface)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--space-border)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px var(--space-shadow)',
      }
    : {};

  return (
    <div
      className={`relative ${fullHeight ? 'min-h-screen' : 'min-h-96'} ${className}`}
    >
      {/* Background Layer */}
      <SpaceBackground
        theme={theme}
        intensity={backgroundIntensity}
        className="absolute inset-0 -z-10"
      />

      {/* Particle Effects - Only on desktop and when enabled */}
      {showParticles && (
        <div className="absolute inset-0 -z-10 hidden lg:block">
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {Array.from({ length: 15 }, (_, i) => {
              // Position particles on sides and edges, avoiding central content
              const zone = Math.random();
              let left, top;
              
              if (zone < 0.4) {
                // Left side (0-20% of screen width)
                left = Math.random() * 20;
                top = Math.random() * 100;
              } else if (zone < 0.8) {
                // Right side (80-100% of screen width)
                left = 80 + Math.random() * 20;
                top = Math.random() * 100;
              } else {
                // Top/bottom edges (full width but edges)
                left = Math.random() * 100;
                top = Math.random() < 0.5 ? Math.random() * 20 : 80 + Math.random() * 20;
              }
              
              return (
                <div
                  key={`particle-${i}`}
                  className="absolute w-1 h-1 rounded-full opacity-40"
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    background: isDark ? '#F76700' : '#7B5D99',
                    animation: `rocket-float ${4 + Math.random() * 6}s ease-in-out infinite`,
                    animationDelay: `${Math.random() * 4}s`,
                  }}
                />
              );
            })}
          </div>

          {/* Subtle Orbit Rings */}
          <div className="absolute inset-0 flex items-center justify-center opacity-20">
            {[1, 2, 3].map((i) => (
              <div
                key={`orbit-${i}`}
                className="absolute border rounded-full"
                style={{
                  width: `${200 + i * 100}px`,
                  height: `${200 + i * 100}px`,
                  borderColor: isDark ? '#7B5D99' : '#6366f1',
                  borderWidth: '1px',
                  animation: `satellite-orbit ${20 + i * 10}s linear infinite`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content Container */}
      <div
        className={`relative z-10 ${maxWidthClasses[maxWidth]} mx-auto ${spacingClasses[spacing]}`}
        style={containerStyles}
      >
        {children}
      </div>
    </div>
  );
};

// Specialized Page Container Variants
export const DashboardContainer: React.FC<Omit<PageContainerProps, 'maxWidth' | 'spacing'>> = (props) => (
  <PageContainer
    {...props}
    maxWidth="xl"
    spacing="normal"
    backgroundIntensity="normal"
  />
);

export const CourseContainer: React.FC<Omit<PageContainerProps, 'maxWidth' | 'spacing'>> = (props) => (
  <PageContainer
    {...props}
    maxWidth="lg"
    spacing="normal"
    backgroundIntensity="normal"
  />
);

export const StudySessionContainer: React.FC<Omit<PageContainerProps, 'maxWidth' | 'spacing' | 'backgroundIntensity' | 'showParticles'>> = (props) => (
  <PageContainer
    {...props}
    maxWidth="lg"
    spacing="normal"
    backgroundIntensity="none"
    showParticles={false}
  />
);

export const AuthContainer: React.FC<Omit<PageContainerProps, 'maxWidth' | 'spacing' | 'fullHeight'>> = (props) => (
  <PageContainer
    {...props}
    maxWidth="md"
    spacing="normal"
    fullHeight={true}
    backgroundIntensity="cosmic"
  />
);

export const FormContainer: React.FC<Omit<PageContainerProps, 'maxWidth' | 'glassmorphism'>> = (props) => (
  <PageContainer
    {...props}
    maxWidth="md"
    glassmorphism={true}
  />
);

// Responsive breakpoint utilities
export const useResponsiveBreakpoints = () => {
  const [screenSize, setScreenSize] = React.useState<'sm' | 'md' | 'lg' | 'xl'>('lg');

  React.useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width < 768) setScreenSize('sm');
      else if (width < 1024) setScreenSize('md');
      else if (width < 1280) setScreenSize('lg');
      else setScreenSize('xl');
    };

    // Set initial value
    updateScreenSize();

    // Listen for window resize
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  return {
    screenSize,
    isMobile: screenSize === 'sm',
    isTablet: screenSize === 'md',
    isDesktop: screenSize === 'lg' || screenSize === 'xl',
    isLargeDesktop: screenSize === 'xl',
  };
};

// Mobile-optimized particle system
export const MobileParticleSystem: React.FC<{ theme: 'light' | 'dark' | 'mixed' }> = ({ theme }) => {
  const isDark = theme === 'dark' || theme === 'mixed';
  
  return (
    <div className="absolute inset-0 -z-10 lg:hidden">
      {/* Reduced particles for mobile performance */}
      {Array.from({ length: 5 }, (_, i) => {
        // Position particles on sides and edges, avoiding central content
        const zone = Math.random();
        let left, top;
        
        if (zone < 0.4) {
          // Left side (0-25% of screen width for mobile)
          left = Math.random() * 25;
          top = Math.random() * 100;
        } else if (zone < 0.8) {
          // Right side (75-100% of screen width for mobile)
          left = 75 + Math.random() * 25;
          top = Math.random() * 100;
        } else {
          // Top/bottom edges (full width but edges)
          left = Math.random() * 100;
          top = Math.random() < 0.5 ? Math.random() * 25 : 75 + Math.random() * 25;
        }
        
        return (
          <div
            key={`mobile-particle-${i}`}
            className="absolute w-0.5 h-0.5 bg-vibrant-orange rounded-full opacity-30"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              animation: `twinkle ${3 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        );
      })}
    </div>
  );
};

export default PageContainer; 